import {
  ChevronDown,
  ChevronRight,
  Languages,
  Lock,
  MessageSquare,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  ShieldCheck,
} from "lucide-react";
import { useRef, useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { AssistantTurn, Rule, TranslationStatus } from "@/lib/api";
import { cn } from "@/lib/utils";

import { conversationsOf, requestTitle, shortWhen, titleCase } from "./utils";
import type { RailView } from "./views";

/*
 * The rail is dark in both states, so collapsing changes its width and nothing
 * else. Tints are white at opacity rather than flat greys: every one then sits
 * on the same base and the rail reads as a single surface.
 *
 * Nothing below 60% on text — under that, white on Commercial Grey stops
 * clearing 4.5:1.
 */
const ITEM_BASE =
  "flex w-full items-center gap-2 rounded-md text-left transition-colors text-white/75 hover:bg-white/10 hover:text-white";
const ITEM_SELECTED = "bg-white/20 text-white font-semibold";

interface RailProps {
  view: RailView;
  onSelect: (view: RailView) => void;
  rules: Rule[];
  recentTurns: AssistantTurn[];
  activeTurnId: string | undefined;
  onOpenRequest: (turn: AssistantTurn) => void;
  onRemoveRequest: (turn: AssistantTurn) => void;
  /** An empty name clears it again, which is how a rename is undone. */
  onRenameRequest: (turn: AssistantTurn, name: string) => void;
  onNewRequest: () => void;
  translationStatus: TranslationStatus | undefined;
  onCollapse: () => void;
}

/**
 * The rail is navigation, not content.
 *
 * Everything it lists opens in the main container instead of expanding in
 * place, so the rail stays scannable however many briefs pile up. Both groups
 * collapse to a single row, so the resting state can be two tabs rather than a
 * wall of links.
 */
export function Rail({
  view,
  onSelect,
  rules,
  recentTurns,
  activeTurnId,
  onOpenRequest,
  onRemoveRequest,
  onRenameRequest,
  onNewRequest,
  translationStatus,
  onCollapse,
}: RailProps) {
  const [guardrailsOpen, setGuardrailsOpen] = useState(true);
  const [requestsOpen, setRequestsOpen] = useState(true);
  const categories = [...new Set(rules.map((rule) => rule.category))];
  const conversations = conversationsOf(recentTurns);
  // The conversation's files are listed in the panel on the right, beside the
  // conversation itself. Listing them here as well put the same file in two
  // places at once, which reads as two different files.

  return (
    <div className="bg-rail flex h-full flex-col">
      <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-4" aria-label="Composer sections">
        <section className="space-y-1">
          <RailGroup
            icon={MessageSquare}
            label="Requests"
            open={requestsOpen}
            onToggle={() => setRequestsOpen((open) => !open)}
          />
          {requestsOpen && (
            <>
              <RailSubItem
                label="New request"
                selected={view.kind === "chat" && activeTurnId === undefined}
                onClick={onNewRequest}
              />
              {/* One row per conversation, not per ask. A follow-up is its own
                  turn on the server, so "now in French" used to arrive as a
                  third row beside the deck it was asking about — three rows
                  that read as three unrelated requests. Newest conversation
                  first, because that is the one someone comes back for. */}
              {conversations.map((conversation) => {
                // Named after the request the conversation opened with, never
                // its newest round: titling it from the latest ask would rename
                // the row under the person reading it, which is the same
                // disorientation the grouping is here to remove.
                const root =
                  conversation.turns.find((turn) => turn.turn_id === conversation.rootId) ??
                  conversation.latest;
                const rounds = conversation.turns.length;
                const when = shortWhen(conversation.latest.created_at);
                return (
                  <ConversationRow
                    key={conversation.rootId}
                    // A chosen name wins; an empty one means nobody chose, so
                    // the title derived from the request comes back — which is
                    // also what clearing a name has to do.
                    name={root.name ?? ""}
                    derivedLabel={requestTitle(root)}
                    title={conversation.turns.map((turn) => turn.request_text).join("\n")}
                    hint={rounds > 1 ? `${rounds} rounds · ${when}` : when}
                    // Selected wherever in the conversation the open turn sits.
                    // Matching only the latest round dropped the highlight the
                    // moment someone reopened an earlier one.
                    selected={
                      view.kind === "chat" &&
                      activeTurnId !== undefined &&
                      conversation.turns.some((turn) => turn.turn_id === activeTurnId)
                    }
                    // Opening lands on the latest round, so the screen shows
                    // where the conversation actually got to rather than the
                    // request it started from.
                    onClick={() => onOpenRequest(conversation.latest)}
                    // The name belongs on the round the row is labelled from —
                    // the conversation's first — or reopening an earlier round
                    // would show the old derived title again.
                    onRename={(name) => onRenameRequest(root, name)}
                    // Removal is still per turn: deleting a whole conversation
                    // destroys files the dialog would have to account for one
                    // by one, so this removes the round it names.
                    onDelete={() => onRemoveRequest(conversation.latest)}
                  />
                );
              })}
              {conversations.length === 0 && (
                <p className="px-3 py-2 text-xs leading-5 text-white/60">
                  Nothing asked yet. Your conversations are kept here — every follow-up grouped with
                  the request it came from — so closing the app does not lose them.
                </p>
              )}
            </>
          )}
        </section>

        <section className="space-y-1">
          <RailGroup
            icon={ShieldCheck}
            label="Guardrails"
            open={guardrailsOpen}
            onToggle={() => setGuardrailsOpen((open) => !open)}
          />
          {/* The rule book only. What a *run* did lives beside that run, in the
              conversation elements panel, not in the app's global furniture. */}
          {guardrailsOpen &&
            categories.map((category) => (
              <RailSubItem
                key={category}
                label={titleCase(category)}
                selected={view.kind === "rules" && view.category === category}
                onClick={() => onSelect({ kind: "rules", category })}
              />
            ))}
        </section>
      </nav>

      <ModelPicker status={translationStatus} />

      <button
        type="button"
        onClick={onCollapse}
        aria-label="Collapse the sidebar"
        aria-expanded
        className="flex shrink-0 items-center gap-2 border-t border-white/15 px-4 py-2.5 text-xs text-white/70 transition-colors hover:bg-white/10 hover:text-white"
      >
        <PanelLeftClose className="h-4 w-4 shrink-0" />
        Collapse
      </button>
    </div>
  );
}

/**
 * The translation model, shown as a control that is deliberately locked.
 *
 * Model choice is a real decision someone will eventually want to make, so it
 * appears as a select rather than a status line — but the pilot runs one
 * approved model, and a disabled control says that more honestly than hiding
 * the choice entirely.
 */
function ModelPicker({ status }: { status: TranslationStatus | undefined }) {
  const model = status?.model ?? "claude-sonnet-5";
  const live = status?.available && status.provider === "anthropic";

  return (
    <div className="shrink-0 border-t border-white/15 px-3 py-3">
      <div className="flex items-center gap-1.5 px-1 pb-1.5">
        <Languages className="h-3.5 w-3.5 text-white/60" />
        <span className="type-label flex-1 text-white/60">Translation model</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Lock className="h-3 w-3 cursor-help text-white/60" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs leading-5">
            Fixed for the pilot. Production would route translation through the approved translation
            service with market tone-of-voice profiles, not a raw model.
          </TooltipContent>
        </Tooltip>
      </div>

      <select
        disabled
        value={model}
        aria-label="Translation model (locked for the pilot)"
        className="w-full cursor-not-allowed rounded-md border border-white/20 bg-white/10 px-2.5 py-1.5 text-xs text-white/70"
      >
        <option value={model}>{model}</option>
      </select>

      <p className="mt-1.5 flex items-center gap-1.5 px-1 text-[11px] text-white/70">
        <span
          className={cn(
            "h-1.5 w-1.5 shrink-0 rounded-full",
            live ? "bg-outcome-applied" : "bg-daylight",
          )}
        />
        {live ? "Live — translating" : "Not configured — strings flagged for review"}
      </p>
    </div>
  );
}

/**
 * The rail when it is closed: the same dark surface, icons only.
 *
 * Every icon either selects its section or reopens the rail, so collapsing is
 * never a dead end.
 */
export function CollapsedRail({
  view,
  onSelect,
  onOpen,
}: {
  view: RailView;
  onSelect: (view: RailView) => void;
  onOpen: () => void;
}) {
  return (
    <nav
      className="bg-rail flex h-full w-14 flex-col items-center gap-1 py-4"
      aria-label="Composer sections"
    >
      <CollapsedItem
        icon={MessageSquare}
        label="Requests"
        selected={view.kind === "chat"}
        onClick={() => onSelect({ kind: "chat" })}
      />
      {/* Both groups are lists, so their icons reopen the rail rather than
          guessing which rule set or brief was wanted. */}
      <CollapsedItem
        icon={ShieldCheck}
        label="Guardrails"
        selected={view.kind === "rules" || view.kind === "run-report"}
        onClick={onOpen}
      />
      {/* The expand control sits with the icons rather than in the header, so
          opening and closing happen in the same place. */}
      <div className="mt-auto">
        <CollapsedItem
          icon={PanelLeftOpen}
          label="Expand sidebar"
          selected={false}
          onClick={onOpen}
        />
      </div>
    </nav>
  );
}

function CollapsedItem({
  icon: Icon,
  label,
  badge,
  selected,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  badge?: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          aria-current={selected ? "page" : undefined}
          className={cn(
            "relative flex h-10 w-10 items-center justify-center rounded-md transition-colors",
            selected
              ? "bg-white/20 text-white"
              : "text-white/75 hover:bg-white/10 hover:text-white",
          )}
        >
          <Icon className="h-4 w-4" />
          {badge ? (
            <span className="text-rail absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold">
              {badge}
            </span>
          ) : null}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

function RailGroup({
  icon: Icon,
  label,
  count,
  open,
  onToggle,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left transition-colors hover:bg-white/10"
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-white/60" />
      <span className="type-label flex-1 text-white/60">{label}</span>
      {count ? (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full border border-white/30 px-1.5 text-[11px] text-white/80">
          {count}
        </span>
      ) : null}
      {open ? (
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-white/60" />
      ) : (
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-white/60" />
      )}
    </button>
  );
}

function RailSubItem({
  label,
  title,
  hint,
  selected,
  onClick,
}: {
  label: string;
  /** The full text the short label stands for, on hover. */
  title?: string;
  hint?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-current={selected ? "page" : undefined}
      className={cn(ITEM_BASE, "items-start py-1.5 pr-2 pl-3 text-sm", selected && ITEM_SELECTED)}
    >
      <ChevronRight
        className={cn(
          "mt-0.5 h-3.5 w-3.5 shrink-0 transition-transform",
          selected ? "translate-x-0.5 text-white" : "text-white/60",
        )}
      />
      <span className="line-clamp-2 min-w-0 flex-1 break-words">{label}</span>
      {hint ? <span className="shrink-0 text-[11px] text-white/60">{hint}</span> : null}
    </button>
  );
}

/**
 * One conversation in the history — openable, renameable, removable.
 *
 * The row is a button, so its actions cannot be nested inside it: a menu
 * button within a button makes the whole history row a target for whatever the
 * menu holds. The trigger therefore sits beside the row and is positioned over
 * its corner.
 */
function ConversationRow({
  name,
  derivedLabel,
  title,
  hint,
  selected,
  onClick,
  onRename,
  onDelete,
}: {
  /** The name someone chose, or `""` when nobody has. */
  name: string;
  /** What the row is called when there is no chosen name. */
  derivedLabel: string;
  /** The full text the short label stands for, on hover. */
  title?: string;
  hint?: string;
  selected: boolean;
  onClick: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
}) {
  // The text in the box while renaming. `undefined` rather than `""` because
  // an empty box is a legitimate thing to be editing — it is how a name gets
  // cleared — so emptiness cannot also mean "not editing".
  const [draft, setDraft] = useState<string | undefined>(undefined);
  // The name just committed, shown before the refetched history carries it.
  // Without this the row snaps back to its old label for as long as the round
  // trip takes, which reads as the rename having failed.
  const [committed, setCommitted] = useState<string | undefined>(undefined);
  // Set while Rename is being chosen, and read as the menu closes. Radix hands
  // focus back to its trigger on close — but by then the trigger has been
  // replaced by the input, so the focus lands nowhere, the input blurs, and
  // blur cancels the rename. This is how that hand-back is declined.
  const openingRename = useRef(false);

  // The server has caught up, so the local echo goes: holding on to it would
  // outrank a rename made anywhere else.
  if (committed !== undefined && committed === name) {
    setCommitted(undefined);
  }

  const chosen = committed ?? name;
  const label = chosen || derivedLabel;

  const commit = () => {
    if (draft === undefined) {
      return;
    }
    const next = draft.trim();
    setDraft(undefined);
    // Enter on an untouched box is agreement, not an edit. Sending it anyway
    // would pin the derived title as a chosen name, and the title would then
    // stop following the request it was derived from.
    if (next === label) {
      return;
    }
    setCommitted(next);
    onRename(next);
  };

  if (draft !== undefined) {
    return (
      <div className={cn(ITEM_BASE, "gap-0 py-1 pr-2 pl-3", selected && ITEM_SELECTED)}>
        <input
          value={draft}
          autoFocus
          // Selected on arrival: renaming usually replaces the whole title, and
          // a caret at one end means clearing it by hand first.
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commit();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              setDraft(undefined);
            }
          }}
          // Cancel, not commit. A click somewhere else is far more often "I
          // changed my mind" than "save this", and a name saved by accident is
          // harder to notice than one that was not saved at all.
          onBlur={() => setDraft(undefined)}
          // The server truncates at 80, so the box says so rather than letting
          // characters be typed and then quietly lost.
          maxLength={80}
          aria-label={`Rename ${label}`}
          className="w-full rounded-sm border border-white/30 bg-white/10 px-1.5 py-0.5 text-sm text-white outline-none focus:border-white/60"
        />
      </div>
    );
  }

  return (
    <div className="group/row relative flex items-stretch">
      <button
        type="button"
        onClick={onClick}
        title={title}
        aria-current={selected ? "page" : undefined}
        className={cn(ITEM_BASE, "items-start py-1.5 pr-7 pl-3 text-sm", selected && ITEM_SELECTED)}
      >
        <ChevronRight
          className={cn(
            "mt-0.5 h-3.5 w-3.5 shrink-0 transition-transform",
            selected ? "translate-x-0.5 text-white" : "text-white/60",
          )}
        />
        <span className="line-clamp-2 min-w-0 flex-1 break-words">{label}</span>
        {hint ? <span className="shrink-0 text-[11px] text-white/60">{hint}</span> : null}
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            // Named after the row it belongs to: "Options" on its own is the
            // same name on every row, which tells a screen reader nothing.
            aria-label={`Options for ${label}`}
            className={cn(
              "absolute top-1 right-1 rounded p-1 text-white/45 opacity-0 transition hover:bg-white/10 hover:text-white",
              // Hidden until wanted, but never unreachable: it appears on
              // hover, on keyboard focus, while its menu is open, and stays put
              // on the selected row so the row you are working in always has
              // its actions in view. `opacity-0` rather than `hidden` is what
              // keeps it in the tab order.
              "group-hover/row:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100",
              selected && "opacity-100",
            )}
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-48"
          onCloseAutoFocus={(event) => {
            if (openingRename.current) {
              event.preventDefault();
              openingRename.current = false;
            }
          }}
        >
          <DropdownMenuItem
            onSelect={() => {
              openingRename.current = true;
              setDraft(label);
            }}
          >
            Rename
          </DropdownMenuItem>
          {/* Still behind the confirmation dialog: this is the one action that
              can destroy a generated file, and the dialog is what asks the
              server what would actually be lost. */}
          <DropdownMenuItem variant="destructive" onSelect={onDelete}>
            Delete from history
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
